import { LitElement, html, css, classMap, t, utils } from '@hh/global.js'
const { getCart, addToCart, reRenderShopifySections, getChildSlot } = utils

class HHPersonalize extends LitElement {
  static properties = {
    enableBottleEngraving: {
      type: Boolean,
      attribute: 'enable-bottle-engraving',
      reflect: true,
    },
    globalDisableBottleEngraving: {
      type: Boolean,
      attribute: 'global-disable-bottle-engraving',
      reflect: true,
    },
    enableMedallionEngraving: {
      type: Boolean,
      attribute: 'enable-medallion-engraving',
      reflect: true,
    },
    globalDisableMedallionEngraving: {
      type: Boolean,
      attribute: 'global-disable-medallion-engraving',
      reflect: true,
    },
    enableEmbossing: {
      type: Boolean,
      attribute: 'enable-embossing',
      reflect: true,
    },
    globalDisableEmbossing: {
      type: Boolean,
      attribute: 'global-disable-embossing',
      reflect: true,
    },
    currentVariant: {
      type: Object,
    },
    defaultVariant: {
      type: Object,
      attribute: 'default-variant'
    },
    hasBottleEngravingCustomization: {
      type: Boolean,
    },
    bottleEngravingCustomization: {
      type: Object,
    },
    hasMedallionEngravingCustomization: {
      type: Boolean,
    },
    medallionEngravingCustomization: {
      type: Object,
    },
    selectedMedallionVariant: {
      type: Object,
    },
    hasLeatherEmbossingCustomization: {
      type: Boolean,
    },
    leatherEmbossingCustomization: {
      type: Object,
    },
    totalPrice: {
      type: Number,
    },
    enableUpsell: {
      type: Boolean,
      attribute: 'enable-upsell',
    },
    hasUpsellCustomization: {
      type: Boolean,
    },
  }

  constructor() {
    super()

    window.addEventListener('variant:change', this.onVariantChange.bind(this))

    this.bottleEngravingCustomization = {}

    this.classes = {
      active: 'active',
    }

    this.metafieldStrings = {
      bottleEngraving: 'Bottle Engraving',
      medallionEngraving: 'Medallion Engraving',
      embossing: 'Embossing',
    }

    this.strings = {
      message1: t('products.product.personalization.message_1'),
      message2: t('products.product.personalization.message_2'),
      initials: t('products.product.personalization.initials'),
      style: t('products.product.personalization.style'),
      color: t('products.product.personalization.color'),
      addToCart: t('products.product.add_to_cart'),
    }
  }

  get opener() {
    return document.querySelector('.js-personalize-opener') ?? null
  }

  get bottleEngravingOption() {
    const slot = this.shadowRoot.querySelector('slot[name="bottle-engraving"]')
    return slot?.assignedElements()[0] ?? null
  }

  get bottleEngravingProduct() {
    const productStriptTag = document.getElementById('bottleEngravingProduct')
    if (!productStriptTag) return null
    return JSON.parse(productStriptTag.innerHTML)[0]
  }

  get medallionEngravingOption() {
    const slot = this.shadowRoot.querySelector(
      'slot[name="medallion-engraving"]'
    )
    return slot?.assignedElements()[0] ?? null
  }

  get medallionEngravingProduct() {
    const productStriptTag = document.getElementById(
      'medallionEngravingProduct'
    )
    if (!productStriptTag) return null
    return JSON.parse(productStriptTag.innerHTML) ?? null
  }

  get leatherEmbossingOption() {
    const slot = this.shadowRoot.querySelector('slot[name="leather-embossing"]')
    return slot?.assignedElements()[0] ?? null
  }

  get leatherEmbossingProduct() {
    const productStriptTag = document.getElementById('leatherEmbossingProduct')
    if (!productStriptTag) return null
    return JSON.parse(productStriptTag.innerHTML)[0]
  }

  get upsellOption() {
    const slot = this.shadowRoot.querySelector('slot[name="upsell"]')
    return slot?.assignedElements()[0] ?? null
  }

  get upsellProduct() {
    const productStriptTag = document.getElementById('upsellProduct')
    if (!productStriptTag) return null
    return JSON.parse(productStriptTag.innerHTML)[0] ?? null
  }

  get submitButton() {
    const slot = this.shadowRoot.querySelector('slot[name="submit"]')
    return slot?.assignedElements()[0] ?? null
  }

  get quantityValue() {
    const quantityWrapper = document.querySelector('.js-pdp-quantity-input')
    const quantityInput = quantityWrapper?.shadowRoot.querySelector('input')
    return quantityInput?.value ?? 1
  }

  connectedCallback() {
    super.connectedCallback()

    this.currentVariant = JSON.parse(utils.decode(this.getAttribute('default-variant')))
    this.totalPrice = this.currentVariant.price

    window.addEventListener('drawer:open', this.onDrawerOpen.bind(this))
    window.addEventListener('submit:engraving', this.onSubmitClick.bind(this))
    window.addEventListener('personalization:confirm', this.onPersonalizationConfirm.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('variant:change', this.onVariantChange)
    window.removeEventListener('drawer:open', this.onDrawerOpen)
    window.removeEventListener('personalization:confirm', this.onPersonalizationConfirm)
  }

  firstUpdated() {
    this.updateTotalPrice(this.totalPrice)
  }

  updated(changedProperties) {
    if (changedProperties.has('bottleEngravingCustomization')) {
      this.hasBottleEngravingCustomization =
        Object.keys(this.bottleEngravingCustomization).length > 0
    }
    if (changedProperties.has('medallionEngravingCustomization')) {
      this.hasMedallionEngravingCustomization =
        Object.keys(this.medallionEngravingCustomization).length > 0
    }
    if (changedProperties.has('leatherEmbossingCustomization')) {
      this.hasLeatherEmbossingCustomization =
        Object.keys(this.leatherEmbossingCustomization).length > 0
    }
    if (
      changedProperties.has('hasBottleEngravingCustomization') ||
      changedProperties.has('hasMedallionEngravingCustomization') ||
      changedProperties.has('hasLeatherEmbossingCustomization') ||
      changedProperties.has('hasUpsellCustomization')
    ) {
      this.updateEngravingCustomization()
    }
    if (changedProperties.has('totalPrice')) {
      this.updateTotalPrice(this.totalPrice)
    }
  }

  onPersonalizationConfirm() {
    this.submitButton.shadowRoot.querySelector('button').focus()
  }

  onVariantChange(e) {
    const { detail } = e
    this.currentVariant = detail.variant
    this.currentVariantMetafields = detail.metafields

    this.toggleEnabledPersonalizationOptions(
      this.currentVariantMetafields,
      this.currentVariant
    )
    this.totalPrice = this.currentVariant.price
  }

  onDrawerOpen(e) {
    if (!e.target.classList.contains('js-personalize-drawer')) return

    const engravingOptions = {
      'bottle-engraving': this.enableBottleEngraving,
      'medallion-engraving': this.enableMedallionEngraving,
      'leather-embossing': this.enableEmbossing
    }

    const enabledOptions = Object.keys(engravingOptions).filter(key => engravingOptions[key])

    if (enabledOptions.length === 1) {
      document.body.setAttribute('drawer', JSON.stringify({ id: `${enabledOptions[0]}-drawer` }))
    }
  }

  toggleEnabledPersonalizationOptions(metafields, variant) {
    const { personalizationOptions } = metafields

    if (personalizationOptions === null || !variant.available) {
      this.enableBottleEngraving = false
      this.enableMedallionEngraving = false
      this.enableEmbossing = false
      this.opener.setAttribute('hidden', '')
      return
    }

    this.enableBottleEngraving =
      !this.globalDisableBottleEngraving &&
      personalizationOptions.includes(this.metafieldStrings.bottleEngraving)

    this.enableMedallionEngraving =
      !this.globalDisableMedallionEngraving &&
      personalizationOptions.includes(this.metafieldStrings.medallionEngraving)

    this.enableEmbossing =
      !this.globalDisableEmbossing &&
      personalizationOptions.includes(this.metafieldStrings.embossing)

    if (
      this.enableBottleEngraving ||
      this.enableMedallionEngraving ||
      this.enableEmbossing
    ) {
      this.opener.removeAttribute('hidden')
    } else {
      this.opener.setAttribute('hidden', '')
    }
  }

  updateEngravingCustomization() {
    if (this.hasBottleEngravingCustomization) {
      this.bottleEngravingOption?.classList?.add(this.classes.active)
    } else {
      this.bottleEngravingOption?.classList?.remove(this.classes.active)
    }

    if (this.hasMedallionEngravingCustomization) {
      this.medallionEngravingOption?.classList?.add(this.classes.active)
    } else {
      this.medallionEngravingOption?.classList?.remove(this.classes.active)
    }

    if (this.hasLeatherEmbossingCustomization) {
      this.leatherEmbossingOption?.classList?.add(this.classes.active)
    } else {
      this.leatherEmbossingOption?.classList?.remove(this.classes.active)
    }

    if (this.hasUpsellCustomization) {
      this.upsellOption?.classList?.add(this.classes.active)
    } else {
      this.upsellOption?.classList?.remove(this.classes.active)
    }

    this.totalPrice = this.calculateTotalPrice()
  }

  addBottleEngravingOption(attributes) {
    this.bottleEngravingCustomization = attributes
    const attributesContainer = this.bottleEngravingOption.querySelector(
      '[data-personalize-attributes]'
    )
    this.addAttributes(attributesContainer, attributes)
  }

  addMedallionEngravingOption(attributes) {
    this.medallionEngravingCustomization = attributes
    this.selectedMedallionVariant = this.medallionEngravingProduct.find(
      (variant) => variant.title === attributes.color
    )
    const attributesContainer = this.medallionEngravingOption.querySelector(
      '[data-personalize-attributes]'
    )
    this.addAttributes(attributesContainer, attributes)
  }

  addLeatherEmbossingOption(attributes) {
    this.leatherEmbossingCustomization = attributes
    const attributesContainer = this.leatherEmbossingOption.querySelector(
      '[data-personalize-attributes]'
    )
    this.addAttributes(attributesContainer, attributes)
  }

  addAttributes(container, attributes) {
    container.innerHTML = ''
    for (let key in attributes) {
      const attribute = attributes[key]
      if (attribute === '') continue

      const attributeElement = document.createElement('li')
      attributeElement.innerHTML = `
        <span class="attribute-name">${this.strings[key]}:</span>
        <span class="attribute-value">${attribute}</span>
      `
      container.appendChild(attributeElement)
    }
  }

  onPersonalizationClick(e) {
    const { target } = e
    const removeButton = target.closest('[data-personalization-remove]')
    if (removeButton) {
      const personalizationType = removeButton.dataset.personalizationRemove
      if (personalizationType === 'bottle-engraving') {
        this.bottleEngravingCustomization = {}
      } else if (personalizationType === 'medallion-engraving') {
        this.medallionEngravingCustomization = {}
      } else if (personalizationType === 'leather-embossing') {
        this.leatherEmbossingCustomization = {}
      }
      this.totalPrice = this.calculateTotalPrice()
    }
    this.dispatchEvent(
      new CustomEvent('double-drawer:open', {
        bubbles: true,
      })
    )
  }

  onUpsellClick(e) {
    const { target } = e
    const upsellAddButton = target.closest('[data-upsell-add]')
    const upsellRemoveButton = target.closest('[data-upsell-remove]')

    if (upsellAddButton) {
      this.hasUpsellCustomization = true
    }
    if (upsellRemoveButton) {
      this.hasUpsellCustomization = false
    }

    this.totalPrice = this.calculateTotalPrice()
  }

  calculateTotalPrice() {
    let totalPrice = this.currentVariant.price
    if (this.hasBottleEngravingCustomization) {
      totalPrice += this.bottleEngravingProduct.price
    }
    if (this.hasMedallionEngravingCustomization) {
      totalPrice += this.medallionEngravingProduct[0].price
    }
    if (this.hasLeatherEmbossingCustomization) {
      totalPrice += this.leatherEmbossingProduct.price
    }
    if (this.hasUpsellCustomization) {
      totalPrice += this.upsellProduct.price
    }
    return totalPrice
  }

  updateTotalPrice() {
    const buttonContent = `${this.strings.addToCart} - ${this.formatMoney(
      this.totalPrice
    )}`
    if (!this.submitButton) return
    this.submitButton.text = buttonContent
  }

  formatMoney(amount) {
    return HH.formatMoney(
      amount,
      window.shopSettings.money_with_currency_format
    )
  }

  randomFixedInteger(length) {
    return Math.floor(
      Math.pow(10, length - 1) +
      Math.random() * (Math.pow(10, length) - Math.pow(10, length - 1) - 1)
    )
  }

  async onSubmitClick() {
    const cart = await getCart();
    const { items } = cart;
    const uuid = crypto.randomUUID();
    const itemKey = this.randomFixedInteger(14);
    const metafields = this.getMetafieldVariantData(this.currentVariant.id);

    const lineItems = [];
    const parentItemProperties = { _is_parent: true, _group_id: uuid, '_attached-item-key': itemKey, '_personalization-id': itemKey };

    const mainVariant = {
      id: this.currentVariant.id,
      quantity: this.quantityValue,
    };

    if (this.hasUpsellCustomization) {
      const properties = {
        _group_id: uuid,
        '_attached-item-key': itemKey,
        '_personalization-id': itemKey
      };

      lineItems.push({
        id: this.upsellProduct.id,
        quantity: this.quantityValue,
        properties: Object.assign(
          properties,
          !!this.upsellProduct.line_properties ?
            (typeof this.upsellProduct.line_properties === 'string' ? JSON.parse(this.upsellProduct.line_properties) : this.upsellProduct.line_properties)
            : {})
      })

      mainVariant.properties = Object.assign(
        { _is_parent: true },
        properties,
        !!metafields.lineProperties ?
          metafields.lineProperties
          : {}
      )
    }

    if (this.hasMedallionEngravingCustomization) {
      const properties = {
        _group_id: uuid,
        '_attached-item-key': itemKey,
        '_personalization-id': itemKey,
        Initials: this.medallionEngravingCustomization.initials,
        Font: this.medallionEngravingCustomization.style,
        _Color: this.medallionEngravingCustomization.color
      };

      lineItems.push({
        id: this.selectedMedallionVariant.id,
        quantity: this.quantityValue,
        properties: Object.assign(
          properties,
          !!this.selectedMedallionVariant.line_properties ?
            (typeof this.selectedMedallionVariant.line_properties === 'string' ? JSON.parse(this.selectedMedallionVariant.line_properties) : this.selectedMedallionVariant.line_properties)
            : {})
      })

      mainVariant.properties = Object.assign(
        { _is_parent: true },
        properties,
        !!metafields.lineProperties ?
          metafields.lineProperties
          : {}
      )
    }

    if (this.hasLeatherEmbossingCustomization) {
      const properties = {
        _group_id: uuid,
        '_attached-item-key': itemKey,
        '_personalization-id': itemKey,
        Initials: this.leatherEmbossingCustomization.initials,
        Font: this.leatherEmbossingCustomization.style
      };

      lineItems.push({
        id: this.leatherEmbossingProduct.id,
        quantity: this.quantityValue,
        properties: Object.assign(
          properties,
          !!this.leatherEmbossingProduct.line_properties ?
            (typeof this.leatherEmbossingProduct.line_properties === 'string' ? JSON.parse(this.leatherEmbossingProduct.line_properties) : this.leatherEmbossingProduct.line_properties) : {})
      })

      mainVariant.properties = Object.assign(
        { _is_parent: true },
        properties,
        !!metafields.lineProperties ?
          (typeof metafields.lineProperties === 'string' ? JSON.parse(metafields.lineProperties) : metafields.lineProperties) : {}
      )
    }

    if (this.hasBottleEngravingCustomization) {
      if (
        this.bottleEngravingProduct &&
        this.bottleEngravingCustomization &&
        this.bottleEngravingCustomization.message1 &&
        this.bottleEngravingCustomization.style
      ) {
        const properties = {
          _group_id: uuid,
          '_attached-item-key': itemKey,
          '_personalization-id': itemKey,
          'Message 1': this.bottleEngravingCustomization.message1,
          'Message 2': this.bottleEngravingCustomization.message2,
          Font: this.bottleEngravingCustomization.style,
          '_Color': metafields.fontColorName
        };

        lineItems.push({
          id: this.bottleEngravingProduct.id,
          quantity: this.quantityValue,
          properties: Object.assign(
            properties,
            !!this.bottleEngravingProduct.line_properties ?
              (typeof this.bottleEngravingProduct.line_properties === 'string' ? JSON.parse(this.bottleEngravingProduct.line_properties) : this.bottleEngravingProduct.line_properties)
            : {})
        })

        mainVariant.properties = Object.assign(
          { _is_parent: true },
          properties,
          !!metafields.lineProperties ?
            metafields.lineProperties
            : {}
        );
      } else {
        console.error('Bottle engraving customization is incomplete:', {
          bottleEngravingProduct: this.bottleEngravingProduct,
          bottleEngravingCustomization: this.bottleEngravingCustomization
        });
      }
    }

    // Handle BXGY Gift logic
    if (metafields.bxgyGifts !== null) {
      const gifts = JSON.parse(window.atob(metafields.bxgyGifts));
      let addableGift = false;
      let bxgy_id = null;

      if (metafields.bxgyGiftQty > 0 && metafields.bxgyReqQty > 0 && gifts.length === 1 && gifts[0].available) {
        addableGift = await this.addableAutoBXGY(this.quantityValue, metafields, gifts[0]);
      }

      if (addableGift) {
        bxgy_id = typeof addableGift === 'boolean' ? this.randomFixedInteger(14) : addableGift;
        parentItemProperties['_bxgy-id'] = bxgy_id;

        const hasBxgy = items.some((item) => item.properties['_is-bxgy']);

        if (typeof addableGift === 'boolean' && !hasBxgy) {
          lineItems.push({
            id: gifts[0].id,
            quantity: 1,
            properties: Object.assign({
              '_is-bxgy': true,
              '_bxgy-id': bxgy_id
            }, !!gifts[0].line_properties ?
              (typeof gifts[0].line_properties === 'string' ? JSON.parse(gifts[0].line_properties) : gifts[0].line_properties)
              : {})
          })
        }
        
      }
    }

    lineItems.push(mainVariant);

    try {
      this.submitButton.setAttribute('disabled', true);
  
      if (Array.isArray(lineItems) && lineItems.length > 0) {
        reRenderShopifySections(await addToCart({ items: lineItems }));
      } else {
        console.error('Line items are empty or malformed:', lineItems);
      }
  
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
      this.openCart();
  
      this.clearPersonalization();
      this.openCart()
    } catch (error) {
        console.error('Failed to add items to the cart during the personalization process. Error details:', error, 'Line items attempted:', lineItems);
        console.error('Error adding items to cart:', error, 'Line items:', lineItems);
        this.submitButton.setAttribute('disabled', false);
    }
  }

    

  openCart() {
    document.body.classList.add('mini-cart-is-visible')

    setTimeout(() => {
      document.querySelector('html').classList.add('overflow-hidden')
      document.body.classList.add('mini-cart-is-active')
      document.querySelector('.js-mega-menu-overlay').classList.add('active')
    });
  }

  clearPersonalization() {
    this.hasBottleEngravingCustomization = false
    this.hasMedallionEngravingCustomization = false
    this.hasLeatherEmbossingCustomization = false
    this.hasUpsellCustomization = false
    this.totalPrice = this.calculateTotalPrice()
  }

  async addableAutoBXGY(quantity, variant, gift) {
    const cart = await getCart()
    const { items } = cart
    const active_qty = Number(quantity) || 1

    // info for main variant
    let bxgy_id = false,
      total_count = active_qty
    const parentItems = items.filter(item => item.variant_id === variant.id)
    parentItems.forEach(item => {
      if (item.properties['_bxgy-id']) {
        bxgy_id = item.properties['_bxgy-id']
      }
      total_count += item.quantity
    })

    // info for gifts
    let gifts_count = 0

    if (bxgy_id) {
      items.forEach(item => {
        if (item.properties['_is-bxgy'] && item.properties['_bxgy-id'] === bxgy_id) {
          gifts_count += item.quantity
        }
      })
    }

    if (
      total_count >= variant.bxgyReqQty &&
      gifts_count === 0 &&
      variant.bxgyGiftQty === 1
    ) {
      return true
    } else {
      return bxgy_id
    }
  }

  getMetafieldVariantData(variant_id) {
    this.metafieldVariantData =
      this.metafieldVariantData ||
      JSON.parse(document.querySelector('#variantMetafields').textContent)

    return this.metafieldVariantData.find(data => data['id'] === Number(variant_id))
  }

  randomFixedInteger(length) {
    return Math.floor(
      Math.pow(10, length - 1) +
      Math.random() * (Math.pow(10, length) - Math.pow(10, length - 1) - 1)
    )
  }

  subtotalTemplate() {
    return html`
      <div class="subtotal">
        <div
          class=${classMap({
      line: true,
      hide: !this.enableBottleEngraving,
    })}
        >
          <span class="line-title">
            ${t('products.product.personalization.engraving')}
          </span>
          <span class="line-price">
            ${this.hasBottleEngravingCustomization
        ? this.formatMoney(this.bottleEngravingProduct.price)
        : ' - '}
          </span>
        </div>
        <div
          class=${classMap({
          line: true,
          hide: !this.enableMedallionEngraving,
        })}
        >
          <span class="line-title">
            ${t('products.product.personalization.medallion')}
          </span>
          <span class="line-price">
            ${this.hasMedallionEngravingCustomization
        ? this.formatMoney(this.selectedMedallionVariant.price)
        : ' - '}
          </span>
        </div>
        <div
          class=${classMap({
          line: true,
          hide: !this.enableEmbossing,
        })}
        >
          <span class="line-title">
            ${t('products.product.personalization.embossing')}
          </span>
          <span class="line-price">
            ${this.hasLeatherEmbossingCustomization
        ? this.formatMoney(this.leatherEmbossingProduct.price)
        : ' - '}
          </span>
        </div>
        <div
          class=${classMap({
          line: true,
          hide: !this.enableUpsell,
        })}
        >
          <span class="line-title">
            ${t('products.product.personalization.leather_sleeve')}
          </span>
          <span class="line-price">
            ${this.hasUpsellCustomization
        ? this.formatMoney(this.upsellProduct.price)
        : ' - '}
          </span>
        </div>
        <div class="line line--variant">
          <span class="line-title"> ${this.currentVariant.name} </span>
          <span class="line-price">
            ${this.formatMoney(this.currentVariant.price)}
          </span>
        </div>
      </div>
    `
  }

  get submitButton() {
    const slot = this.shadowRoot.querySelector('slot[name="submit"]')
    return slot?.assignedElements()[0] ?? null
  }

  render() {
    return html`
      <div class="container">
        <div class="options">
          <slot name="heading"></slot>
          <div
            class=${classMap({
      hide: !this.enableBottleEngraving,
    })}
          >
            <slot
              name="bottle-engraving"
              @click=${this.onPersonalizationClick}
            ></slot>
          </div>
          <div
            class=${classMap({
      hide: !this.enableMedallionEngraving,
    })}
          >
            <slot
              name="medallion-engraving"
              @click=${this.onPersonalizationClick}
            ></slot>
          </div>
          <div
            class=${classMap({
      hide: !this.enableEmbossing,
    })}
          >
            <slot
              name="leather-embossing"
              @click=${this.onPersonalizationClick}
            ></slot>
          </div>
        </div>
        <slot name="upsell" @click=${this.onUpsellClick}></slot>
        <div class="footer">
          ${this.subtotalTemplate()}
          <slot name="submit" @click=${this.onSubmitClick}></slot>
        </div>
      </div>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
    }

    :host *,
    :host *::before,
    :host *::after {
      box-sizing: inherit;
    }

    .container {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .options {
      padding: 1.5000rem 1.2500rem;
      flex: 1 0 auto;
      overflow-y: auto;
      height: 0;
    }

    @media (min-width: 990px) {
      .options {
        padding: 2.5000rem 2.2500rem;
      }
    }

    .subtotal {
      padding: 1.2500rem 1.7500rem;
      border-top: 1px solid var(--hh-color-brand-tertiary);
    }

    @media (min-width: 990px) {
      .subtotal {
        padding: 1.8750rem 2.2500rem;
      }
    }

    .line {
      display: flex;
      justify-content: space-between;
      font: var(--hh-body-small-font);
      letter-spacing: var(--hh-body-small-letter-spacing);
      margin: 0 0 0.9375rem;
    }

    .line--variant {
      color: var(--hh-color-subdued-secondary);
      margin: 0;
    }

    .hide {
      display: none;
    }
  `
}

customElements.define('hh-personalize', HHPersonalize)
